import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Turnstile } from "@/components/ui/Turnstile";

export const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    if (!turnstileToken) {
      toast({
        title: "Verification required",
        description: "Please complete the security check",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-newsletter', {
        body: { email, source: 'homepage_newsletter', turnstileToken }
      });

      if (error) throw error;

      toast({
        title: data.alreadySubscribed ? "Already subscribed!" : "Success!",
        description: data.message,
      });

      if (!data.alreadySubscribed) {
        setEmail("");
        setTurnstileToken(null);
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-slate-900 text-white py-12 relative">
      <div className="absolute top-0 left-0 right-0 h-8 bg-slate-900">
        <svg className="w-full h-full" viewBox="0 0 1440 100" preserveAspectRatio="none">
          <path d="M0,50 Q360,0 720,50 T1440,50 L1440,100 L0,100 Z" fill="#0f172a" />
        </svg>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Sign up & save up to 10% on your first order!
            </h2>
            <p className="text-white/80">
              Join our newsletter and get first access to exclusive product drops & deals!
            </p>
          </div>
          <div className="flex-1 w-full max-w-md">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    className="bg-white text-foreground flex-1 h-12"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 h-12 whitespace-nowrap"
                    disabled={isSubmitting || !turnstileToken}
                  >
                    {isSubmitting ? "Subscribing..." : "Save up to 10%"}
                  </Button>
                </div>
                <Turnstile 
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
              </div>
            </form>
            <p className="text-xs text-white/60 mt-2 italic">
              We respect your data and privacy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
